<app-legal-page-layout
  kicker="Legal"
  title="Community Guidelines"
  description="How Safeword moderates content, handles reports, investigates complaints and manages appeals."
  [metaLines]="['Operated by Hove Retail Limited']"
  [sections]="sections">

  <section id="introduction">
    <h2>Introduction</h2>
    <p>
      Safeword is committed to maintaining a safe, lawful and respectful community while supporting freedom of
      lawful expression within the BDSM, fetish and kink communities.
    </p>
    <p>
      To achieve this, Safeword uses a combination of artificial intelligence ("AI"), automated detection
      technologies and trained human moderators to help identify, investigate and respond to content or behaviour
      that may breach our <a routerLink="/legal/terms">Terms of Service</a>, Community Standards or applicable law.
    </p>
    <p>
      AI is used to assist our moderation processes. It does not replace human judgement in situations where
      context, proportionality or discretion are required.
    </p>
    <p>
      We encourage members to report content or behaviour that they believe breaches our Terms of Service,
      Community Standards or applicable law. All reports are treated seriously and are reviewed fairly,
      proportionately and, where appropriate, confidentially.
    </p>
    <p>
      The submission of a report does not automatically result in enforcement action. Each report is assessed on
      its individual facts and circumstances.
    </p>
  </section>

  <section id="how-we-moderate">
    <h2>How We Moderate Content</h2>

    <h3>Purpose of AI-Assisted Moderation</h3>
    <p>AI-assisted moderation helps Safeword identify activity that may require further review.</p>
    <p>This may include detecting:</p>
    <ul>
      <li>suspected child sexual abuse material (CSAM);</li>
      <li>suspected non-consensual intimate images;</li>
      <li>content that may involve coercion or exploitation;</li>
      <li>suspected grooming behaviour;</li>
      <li>spam and unsolicited commercial activity;</li>
      <li>phishing attempts;</li>
      <li>fraudulent accounts;</li>
      <li>impersonation;</li>
      <li>coordinated abusive behaviour;</li>
      <li>malicious software or harmful links;</li>
      <li>copyright infringement;</li>
      <li>repeated policy violations;</li>
      <li>suspicious account activity;</li>
      <li>unusual patterns of platform abuse; and</li>
      <li>other activity that may threaten the safety or integrity of the Services.</li>
    </ul>
    <p>
      AI systems are intended to help prioritise potentially harmful content for review and improve the efficiency
      of our moderation processes.
    </p>

    <h3>Human Moderation</h3>
    <p>
      Safeword employs trained human moderators who review reports, investigate potential breaches and make
      moderation decisions where appropriate.
    </p>
    <p>Human moderators may:</p>
    <ul>
      <li>review content reported by members;</li>
      <li>assess content identified by AI systems;</li>
      <li>investigate complaints;</li>
      <li>consider contextual information;</li>
      <li>review evidence submitted during appeals;</li>
      <li>apply Community Standards consistently;</li>
      <li>determine appropriate enforcement action; and</li>
      <li>escalate matters requiring specialist review.</li>
    </ul>
    <p>Our moderators are expected to act fairly, consistently and in accordance with our internal moderation procedures.</p>

    <h3>Human Oversight</h3>
    <p>Safeword recognises that automated systems are not infallible.</p>
    <p>AI may incorrectly identify lawful content as problematic or fail to identify content that breaches our policies.</p>
    <p>
      For this reason, significant moderation decisions are ordinarily subject to appropriate human oversight
      before permanent enforcement action is taken, unless immediate action is reasonably necessary to:
    </p>
    <ul>
      <li>protect members from imminent harm;</li>
      <li>prevent the distribution of illegal content;</li>
      <li>comply with legal obligations;</li>
      <li>preserve evidence;</li>
      <li>respond to cybersecurity incidents; or</li>
      <li>protect the integrity of the platform.</li>
    </ul>

    <h3>Automated Detection Technologies</h3>
    <p>Safeword may use automated technologies to assist with:</p>
    <ul>
      <li>image analysis;</li>
      <li>video analysis;</li>
      <li>behavioural analysis;</li>
      <li>spam detection;</li>
      <li>fraud detection;</li>
      <li>malware detection;</li>
      <li>link analysis;</li>
      <li>duplicate content detection;</li>
      <li>image hashing;</li>
      <li>account security monitoring; and</li>
      <li>detection of automated bots.</li>
    </ul>
    <p>These technologies are designed to assist our moderation and security teams and improve the safety of the Services.</p>
  </section>

  <section id="reporting-content">
    <h2>Reporting Content</h2>

    <h3>What You Can Report</h3>
    <p>
      Members play an important role in helping maintain a safe community. Members may report content, accounts or
      activity that they believe may breach our policies or applicable law, including:
    </p>
    <ul>
      <li>content;</li>
      <li>messages, including private messages;</li>
      <li>comments;</li>
      <li>photographs, videos or live streams;</li>
      <li>profile information or usernames;</li>
      <li>events or event listings;</li>
      <li>marketplace listings;</li>
      <li>creators or creator accounts;</li>
      <li>advertisements;</li>
      <li>groups or communities;</li>
      <li>harassment;</li>
      <li>scams;</li>
      <li>impersonation;</li>
      <li>spam or unsolicited commercial activity;</li>
      <li>suspected illegal activity; or</li>
      <li>any other content, behaviour or activity available through the Services that may breach our rules.</li>
    </ul>
    <p>
      Reports may be submitted using the reporting tools provided within the Safeword platform or by contacting our
      support team. Reports are reviewed in accordance with our moderation procedures and may be prioritised
      according to the nature and seriousness of the report.
    </p>

    <h3>Reasons for Reporting</h3>
    <p>Members may submit reports for a variety of reasons, including where content or behaviour is believed to involve:</p>
    <ul>
      <li>child sexual abuse material (CSAM);</li>
      <li>persons under the age of 18;</li>
      <li>non-consensual intimate images;</li>
      <li>revenge pornography;</li>
      <li>coercion or exploitation;</li>
      <li>human trafficking;</li>
      <li>harassment or stalking;</li>
      <li>threats of violence;</li>
      <li>hate speech;</li>
      <li>discrimination;</li>
      <li>fraud or scams;</li>
      <li>phishing attempts;</li>
      <li>impersonation;</li>
      <li>copyright infringement;</li>
      <li>trademark infringement;</li>
      <li>deepfake or manipulated intimate media;</li>
      <li>malicious software or harmful links;</li>
      <li>spam or unsolicited commercial activity;</li>
      <li>unlawful content; or</li>
      <li>any other breach of our Terms of Service or Community Standards.</li>
    </ul>
    <p>These examples are not exhaustive.</p>
  </section>

  <section id="investigation-process">
    <h2>Investigation Process</h2>

    <h3>Investigation of Reports</h3>
    <p>When a report is received, Safeword may:</p>
    <ul>
      <li>acknowledge receipt of the report where appropriate;</li>
      <li>assess the information provided;</li>
      <li>review the reported content;</li>
      <li>consider relevant contextual information;</li>
      <li>use AI-assisted tools to help prioritise or identify related concerns;</li>
      <li>request additional information from the reporting party where appropriate;</li>
      <li>review previous moderation history where relevant;</li>
      <li>preserve evidence where required; and</li>
      <li>determine whether further investigation is necessary.</li>
    </ul>
    <p>The depth of any investigation will depend upon the seriousness and nature of the reported issue.</p>

    <h3>AI-Assisted Review</h3>
    <p>Safeword uses AI-assisted technologies to help identify content that may require attention.</p>
    <p>AI may assist by:</p>
    <ul>
      <li>identifying potentially prohibited content;</li>
      <li>detecting patterns of abuse;</li>
      <li>identifying repeated policy violations;</li>
      <li>prioritising urgent reports;</li>
      <li>detecting spam and fraud; and</li>
      <li>assisting moderators in reviewing large volumes of content.</li>
    </ul>
    <p>AI supports, but does not replace, human moderation.</p>

    <h3>Human Review</h3>
    <p>Where appropriate, reports are reviewed by trained human moderators.</p>
    <p>Human moderators consider:</p>
    <ul>
      <li>the available evidence;</li>
      <li>the context in which content was published;</li>
      <li>applicable law;</li>
      <li>our Terms of Service;</li>
      <li>our Community Standards;</li>
      <li>previous enforcement history where relevant; and</li>
      <li>any additional information submitted during the investigation.</li>
    </ul>
    <p>Moderators aim to make decisions that are fair, consistent and proportionate.</p>
  </section>

  <section id="possible-outcomes">
    <h2>Possible Outcomes</h2>
    <p>Following an investigation, Safeword may decide to:</p>
    <ul>
      <li>take no further action;</li>
      <li>request additional information;</li>
      <li>issue guidance or an educational warning;</li>
      <li>temporarily restrict certain features;</li>
      <li>remove or restrict access to content;</li>
      <li>suspend creator monetisation;</li>
      <li>temporarily suspend an account;</li>
      <li>permanently terminate an account;</li>
      <li>preserve evidence for legal purposes;</li>
      <li>report the matter to relevant authorities where required or permitted by law; or</li>
      <li>take any other action reasonably necessary to protect members or comply with legal obligations.</li>
    </ul>
    <p>
      The action taken will depend upon the seriousness of the breach, any previous violations and the overall
      circumstances of the case.
    </p>
  </section>

  <section id="illegal-content">
    <h2>Illegal Content</h2>
    <p>
      Where Safeword becomes aware of content that appears to be illegal, we may take immediate action, including:
    </p>
    <ul>
      <li>removing the content;</li>
      <li>restricting access;</li>
      <li>suspending or terminating accounts;</li>
      <li>preserving evidence where appropriate;</li>
      <li>reporting the matter to relevant authorities where legally required; and</li>
      <li>cooperating with lawful investigations.</li>
    </ul>
    <p>
      Safeword reserves the right to take immediate protective action where necessary to safeguard members or
      comply with legal obligations.
    </p>
  </section>

  <section id="emergency-action">
    <h2>Emergency Action</h2>
    <p>Safeword may take immediate action without prior notice where we reasonably believe it is necessary to:</p>
    <ul>
      <li>protect members from imminent harm;</li>
      <li>prevent the continued distribution of illegal content;</li>
      <li>comply with legal obligations;</li>
      <li>preserve evidence;</li>
      <li>protect the security or integrity of the platform; or</li>
      <li>prevent significant financial or reputational harm.</li>
    </ul>
    <p>
      Immediate action may include temporary removal of content or temporary suspension of accounts while an
      investigation is completed.
    </p>
  </section>

  <section id="fraud-prevention">
    <h2>Fraud Prevention</h2>
    <p>Moderation systems may also assist with detecting:</p>
    <ul>
      <li>fake accounts;</li>
      <li>account takeovers;</li>
      <li>payment fraud;</li>
      <li>chargeback abuse;</li>
      <li>coordinated scams;</li>
      <li>phishing activity;</li>
      <li>identity fraud;</li>
      <li>marketplace fraud;</li>
      <li>other dishonest or unlawful conduct.</li>
    </ul>
    <p>Information may be shared with relevant service providers or authorities where necessary and permitted by law.</p>
  </section>

  <section id="fairness-consistency">
    <h2>Fairness and Consistency</h2>
    <p>Safeword is committed to applying its Community Standards fairly and consistently.</p>
    <p>Moderation decisions are made having regard to:</p>
    <ul>
      <li>the seriousness of the conduct;</li>
      <li>the available evidence;</li>
      <li>the context of the content;</li>
      <li>previous policy violations where relevant;</li>
      <li>applicable law; and</li>
      <li>the safety of our members.</li>
    </ul>
    <p>Each case is assessed on its own facts.</p>
  </section>

  <section id="appeals">
    <h2>Appeals</h2>
    <p>Where appropriate, members may appeal certain moderation decisions affecting their content or account.</p>
    <p>Appeals should include any information that the member believes is relevant to the decision.</p>
    <p>
      Where reasonably practicable, appeals will be reviewed by a trained human moderator who was not involved in
      the original decision.
    </p>
    <p>Additional evidence submitted during the appeal may be considered before a final decision is reached.</p>
    <p>Submitting an appeal does not guarantee that the original decision will be changed.</p>
    <p>Safeword's decision following the conclusion of the appeal process will normally be final.</p>
  </section>

  <section id="false-malicious-reports">
    <h2>False or Malicious Reports</h2>
    <p>Safeword encourages responsible use of its reporting tools.</p>
    <p>Knowingly submitting false, misleading or malicious reports may itself constitute a breach of our Terms of Service.</p>
    <p>
      Repeated abuse of the reporting system may result in warnings, restrictions or suspension of reporting
      privileges and, in serious cases, further enforcement action against the reporting account.
    </p>
  </section>

  <section id="confidentiality">
    <h2>Confidentiality</h2>
    <p>Safeword will generally treat reports confidentially.</p>
    <p>
      Information relating to reports will be shared only with those who require access for the purposes of
      investigating the matter, enforcing our policies or complying with legal obligations.
    </p>
    <p>
      We may be unable to maintain confidentiality where disclosure is required by law or is necessary to protect
      the safety of individuals or the integrity of an investigation.
    </p>
  </section>

  <section id="cooperation-authorities">
    <h2>Cooperation with Authorities</h2>
    <p>
      Where required or permitted by law, Safeword may cooperate with law enforcement agencies, regulatory
      authorities, safeguarding organisations or other competent authorities.
    </p>
    <p>
      Such cooperation may include preserving evidence, providing information where legally required and taking
      appropriate steps to prevent further unlawful activity.
    </p>
  </section>

  <section id="continuous-improvement">
    <h2>Continuous Improvement</h2>
    <p>
      Safeword continually reviews and improves its moderation, reporting, complaints and appeals procedures. This
      may include updating AI models, improving moderator training, refining internal procedures and adopting new
      technologies designed to improve accuracy, consistency, fairness and user safety.
    </p>
    <p>
      We monitor developments in technology, regulation and industry best practice, along with trends and outcomes
      across our own moderation and reporting processes, to help ensure our systems remain effective, proportionate
      and compliant with applicable law.
    </p>
  </section>

  <section id="our-commitment">
    <h2>Our Commitment</h2>
    <p>
      We believe that effective moderation and reporting require both technology and human judgement. Our aim is to
      create an environment where lawful adult expression can flourish while protecting members from illegal
      content, abuse, exploitation, fraud and other harmful activity.
    </p>
    <p>
      Safeword is committed to balancing privacy, freedom of expression, user safety and legal compliance through
      transparent, accountable and continually improving moderation, reporting and appeals practices, respecting
      the rights of all parties involved.
    </p>
    <p>
      Our aim is to foster a platform where members feel confident that concerns will be considered carefully,
      investigated appropriately and resolved as fairly and consistently as possible.
    </p>
  </section>

</app-legal-page-layout>
